/**
 * The diagnosis line generated from the dental chart, in brief Indian format
 * (CLAUDE.md: diagnosis_label in brief Indian format):
 *
 *   "Irreversible pulpitis wrt 36; Caries wrt 16, 26; Gr II mobility wrt 41"
 *
 * Disease conditions only. Existing state (crown, RCT done, missing, implant)
 * is examination, not diagnosis, and stays in Dental Findings.
 *
 * The line lives inside the doctor's Diagnosis text. The page remembers the
 * line it last wrote and replaces exactly that, so the doctor's own words
 * around it are never touched. If the doctor edits the generated line, it is
 * theirs: the next chart change adds a fresh line instead of overwriting it.
 */
import { DENTAL_CONDITIONS } from "../constants/dental.constants";
import type { DentalFinding } from "../schemas/dental.schema";

/** "wrt" (with respect to), the usual Indian dental shorthand. */
const TOOTH_JOIN = "wrt";
const SEPARATOR = "; ";
const ROMAN = ["", "I", "II", "III"];

export function buildDentalDiagnosis(findings: readonly DentalFinding[]): string {
  const parts: string[] = [];
  for (const c of DENTAL_CONDITIONS) {
    if (c.group !== "disease") continue;
    const teeth = findings.filter((f) => f.conditions.includes(c.key)).map((f) => f.tooth).sort((a, b) => a - b);
    if (teeth.length) parts.push(`${c.label} ${TOOTH_JOIN} ${teeth.join(", ")}`);
  }
  for (const grade of [3, 2, 1] as const) {
    const teeth = findings.filter((f) => f.mobility_grade === grade).map((f) => f.tooth).sort((a, b) => a - b);
    if (teeth.length) parts.push(`Gr ${ROMAN[grade]} mobility ${TOOTH_JOIN} ${teeth.join(", ")}`);
  }
  return parts.join(SEPARATOR);
}

/**
 * Replace the previously generated line with the new one, keeping the rest.
 * Matching is on whole "; "-separated segments, so a line the doctor edited
 * ("Caries wrt 16 (incipient)") never matches and is never overwritten.
 */
export function mergeDiagnosisLine(text: string, previous: string, next: string): string {
  const segments = (text ?? "").split(SEPARATOR).map((s) => s.trim()).filter(Boolean);
  const prevSegments = previous ? previous.split(SEPARATOR) : [];
  const at = prevSegments.length ? indexOfRun(segments, prevSegments) : -1;
  if (at < 0 && !next) return text ?? "";
  if (at >= 0) segments.splice(at, prevSegments.length, ...(next ? [next] : []));
  else if (next) segments.unshift(next);
  return segments.join(SEPARATOR);
}

function indexOfRun(haystack: string[], needle: string[]): number {
  for (let i = 0; i + needle.length <= haystack.length; i++) {
    if (needle.every((n, j) => haystack[i + j] === n)) return i;
  }
  return -1;
}
